import React, { useState, useEffect, useRef } from 'react';
import { FaInstagram, FaFacebookF } from 'react-icons/fa';
import { FiMenu, FiX, FiChevronDown, FiChevronUp, FiChevronRight } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import logo from '../../Assests/Moyerlogo.png'
import './Navbar.css';
import './LogoSizing.css';
import './NavbarAccessibility.css';

const TRANSPARENT_PAGES = [
  '/headshot', '/video', '/photography', '/restaurant',
  '/photography/drone', '/photography/wedding', '/photography/headshot'
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [photographyOpen, setPhotographyOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobilePhotographyOpen, setMobilePhotographyOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const servicesRef = useRef(null);
  
  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => {
    setIsOpen(false);
    setMobileServicesOpen(false);
    setMobilePhotographyOpen(false);
  };
  
  const toggleServices = () => setServicesOpen(!servicesOpen);
  const toggleMobileServices = () => setMobileServicesOpen(!mobileServicesOpen);
  const toggleMobilePhotography = () => setMobilePhotographyOpen(!mobilePhotographyOpen);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Check if current page should have transparent navbar
    const shouldBeTransparent = TRANSPARENT_PAGES.some(page => 
      location.pathname.includes(page)
    );

    if (shouldBeTransparent) {
      window.addEventListener('scroll', handleScroll);
      // Set initial state based on scroll position
      handleScroll();
    } else {
      setScrolled(true); // Always solid on other pages
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled, location.pathname]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setServicesOpen(false);
        setPhotographyOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close menu when route changes
  useEffect(() => {
    closeMenu();
    setServicesOpen(false);
    setPhotographyOpen(false);
  }, [location.pathname]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setServicesOpen(false);
        setPhotographyOpen(false);
        if (isOpen) closeMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const servicesItems = [
 
    { name: 'Video Production', link: '/video' },
    { 
      name: 'Photography Services', 
    
      submenu: [
        { name: 'Drone Photography', link: '/drone-photography' },
        { name: 'Wedding Photography', link: '/wedding-photography' },
        { name: 'Headshot Photography', link: '/headshot' },
        { name: 'Photography Production', link: '/photography' },

      ]
    },
    { name: 'Restaurant Marketing', link: '/restaurant' },
  ];

  // Check if current path is a photography subpage
  const isPhotographyActive = location.pathname.startsWith('/photography');

  // Determine navbar class based on scroll state and current page
  const shouldBeTransparent = TRANSPARENT_PAGES.some(page => 
    location.pathname.includes(page)
  );
  const navbarClass = shouldBeTransparent 
    ? `navbar ${scrolled ? 'navbar--solid' : 'navbar--transparent'}`
    : 'navbar';

  return (
    <header className={navbarClass}>
      <div className="navbar__container">
        {/* Logo */}
        <a href="/" className="navbar__logo">
          <img 
            src={logo}
            alt="Moyer Production Co. home" 
            className={shouldBeTransparent && !scrolled ? 'logo--light' : ''}
          />
        </a>

        {/* Desktop Menu */}
        <nav className="navbar__menu" aria-label="Primary navigation">
          <ul className="navbar__list">
            <li><a href="/" className="navbar__link">Home</a></li>
            
            <li 
              className="navbar__dropdown" 
              ref={servicesRef}
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => {
                setServicesOpen(false);
                setPhotographyOpen(false);
              }}
            >
              <button 
                className="navbar__link navbar__dropdown-toggle"
                onClick={toggleServices}
                aria-expanded={servicesOpen}
                aria-controls={servicesOpen ? 'desktop-services' : undefined}
                
              >
                Services
                {servicesOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>
              
              {servicesOpen && (
                <ul className="navbar__dropdown-menu" id="desktop-services">
                  {servicesItems.map((item, index) => (
                    <li 
                      key={index} 
                      className={item.submenu ? 'navbar__dropdown-item--has-submenu' : ''}
                      onMouseEnter={() => item.submenu && setPhotographyOpen(true)}
                      onMouseLeave={() => item.submenu && setPhotographyOpen(false)}
                    >
                      {item.submenu ? (
                        <>
                          <button 
                            type="button"
                            onClick={() => setPhotographyOpen((open) => !open)} 
                            className={`navbar__dropdown-link ${isPhotographyActive ? 'navbar__dropdown-link--active' : ''}`}
                            
                            aria-expanded={photographyOpen}
                          >
                            {item.name}
                            <FiChevronRight className="navbar__dropdown-arrow" />
                          </button>
                          
                          {photographyOpen && (
                            <ul className="navbar__submenu">
                              {item.submenu.map((subItem, subIndex) => (
                                <li key={subIndex}>
                                  <a 
                                    href={subItem.link} 
                                    className={`navbar__submenu-link ${location.pathname === subItem.link ? 'navbar__submenu-link--active' : ''}`}
                                    
                                  >
                                    {subItem.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      ) : (
                        <a 
                          href={item.link} 
                          className={`navbar__dropdown-link ${location.pathname === item.link ? 'navbar__dropdown-link--active' : ''}`}
                          
                        >
                          {item.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
            
          {/*<li><a href="/packages" className="navbar__link">Quarterly Packages</a></li>*/}  
            {/*  <li><a href="/pricing" className="navbar__link">Pricing</a></li>*/}  
            <li><a href="/moyer-blogs" className="navbar__link">Blogs</a></li>
             <li><a href="/moyer-projects" className="navbar__link">Projects</a></li>
            <li><a href="/contact" className="navbar__link">Contact</a></li>
          </ul>
        </nav>

        {/* Social Icons */}
        <div className="navbar__social">
          <a href="https://www.instagram.com/moyerproductionco" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <FaInstagram className="navbar__social-icon" />
          </a>
          <a href="https://www.facebook.com/MoyerEventProductionCo" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <FaFacebookF className="navbar__social-icon" />
          </a>
        </div>

        {/* Mobile Toggle */}
        <button 
          className={`navbar__toggle ${shouldBeTransparent && !scrolled ? 'navbar__toggle--light' : ''}`} 
          onClick={toggleMenu}
          aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
          aria-controls="mobile-navigation"
          aria-expanded={isOpen}
        >
          {isOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      {/* Mobile Menu */}
      <nav id="mobile-navigation" aria-label="Mobile navigation" aria-hidden={!isOpen} inert={!isOpen} className={`navbar__mobile ${isOpen ? 'is-open' : ''}`}>
        <ul className="navbar__mobile-list">
          <li><a href="/" className="navbar__mobile-link" onClick={closeMenu}>Home</a></li>
          
          <li className="navbar__mobile-dropdown">
            <button 
              className="navbar__mobile-link navbar__mobile-dropdown-toggle"
              onClick={toggleMobileServices}
              aria-expanded={mobileServicesOpen}
              aria-controls="mobile-services"
              
            >
              <span>Services</span>
              {mobileServicesOpen ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            
            <ul id="mobile-services" aria-hidden={!mobileServicesOpen} inert={!mobileServicesOpen} className={`navbar__mobile-dropdown-menu ${mobileServicesOpen ? 'is-open' : ''}`}>
              {servicesItems.map((item, index) => (
                <li key={index}>
                  {item.submenu ? (
                    <>
                      <button 
                        className="navbar__mobile-link navbar__mobile-dropdown-toggle"
                        onClick={toggleMobilePhotography}
                        aria-expanded={mobilePhotographyOpen}
                        aria-controls="mobile-photography"
                        
                      >
                        <span>{item.name}</span>
                        {mobilePhotographyOpen ? <FiChevronUp /> : <FiChevronDown />}
                      </button>
                      
                      <ul id="mobile-photography" aria-hidden={!mobilePhotographyOpen} inert={!mobilePhotographyOpen} className={`navbar__mobile-submenu ${mobilePhotographyOpen ? 'is-open' : ''}`}>
                        {item.submenu.map((subItem, subIndex) => (
                          <li key={subIndex}>
                            <a 
                              href={subItem.link} 
                              className="navbar__mobile-dropdown-link"
                              onClick={closeMenu}
                              
                            >
                              {subItem.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <a 
                      href={item.link} 
                      className="navbar__mobile-dropdown-link"
                      onClick={closeMenu}
                      
                    >
                      {item.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </li>
          
        {/*   <li><a href="/packages" className="navbar__mobile-link" onClick={closeMenu}>Quarterly Packages</a></li> */}
        {/* <li><a href="/pricing" className="navbar__mobile-link" onClick={closeMenu}>Pricing</a></li> */}  
          <li><a href="/moyer-blogs" className="navbar__mobile-link" onClick={closeMenu}>Blogs</a></li>
          <li><a href="/moyer-projects" className="navbar__mobile-link" onClick={closeMenu}>Projects</a></li>
          <li><a href="/contact" className="navbar__mobile-link" onClick={closeMenu}>Contact</a></li>
        </ul>

        <div className="navbar__mobile-social">
          <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <FaInstagram className="navbar__mobile-social-icon" />
          </a>
          <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <FaFacebookF className="navbar__mobile-social-icon" />
          </a>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
